import {
  memo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';

import { UK_INSIGHTS, UK_INSIGHTS_TRIAL } from '../../../../../../data/features';
import { UKDisableMessage } from '../../../../../../data/ukPrivateCompanyInsights';
import { perPageFilterDataTablet } from '../../../../../../data/directory/publications/publications';
import { copy } from '../../../../../../helpers/copy';

import Preloader from '../../../../../Preloader';
import PerPage from '../../../../PremiumPublications/Pagination/PerPage';
import TableSection from './TableSection';
import TabTableSection from './TabTableSection';
import ConditionalTooltip from '../../../../../UKPrivateCompanyInsights/CommonComponent/ConditionalTooltip';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const Investors = (props) => {
  const { currentData, setCurrentData, totalValue, isWatchlist } = props;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const hasInsightsAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS)
  ));
  const hasInsightsTrialAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS_TRIAL)
  )) && !hasInsightsAccess;
  const width = useSelector(({ common }) => (
    common.width
  ));

  const hasNextPage = (currentData.data || []).length > (currentPage * Number(pageSize));

  const pageSizeChange = (value) => {
    if (!hasInsightsAccess) return;
    setPageSize(value);
    setCurrentPage(1);
  };

  const currentPageChange = (type) => {
    if (currentData.loading || !hasInsightsAccess) return;
    if (type === 'prev') {
      if (currentPage === 1) return;
      setCurrentPage(currentPage - 1);
    } else {
      if (!hasNextPage) return;
      setCurrentPage(currentPage + 1);
    }
  };

  const onSort = (field) => {
    if (!hasInsightsAccess || hasInsightsTrialAccess) return;

    const data = copy(currentData.data);
    let newSortOrder = sortOrder;

    if (sortField === field && newSortOrder === 'asc') {
      newSortOrder = newSortOrder === 'asc' ? 'desc' : 'asc';
      data.sort((a, b) => {
        if (a[field] === null || a[field] === undefined) {
          return 1;
        }
        if (b[field] === null || b[field] === undefined) {
          return -1;
        }
        return (a[field] < b[field] ? Number(a[field] !== b[field]) : -1);
      });
    } else {
      newSortOrder = 'asc';
      data.sort((a, b) => {
        if (a[field] === null || a[field] === undefined) {
          return 1;
        }
        if (b[field] === null || b[field] === undefined) {
          return -1;
        }
        return (a[field] < b[field] ? -1 : Number(a[field] !== b[field]));
      });
    }
    setCurrentData({
      ...currentData,
      data,
    });
    setSortOrder(newSortOrder);
    setSortField(field);
  };

  const disableMessage = UKDisableMessage(hasInsightsTrialAccess);

  return (
    <div className={cx('investors', { no_access: !hasInsightsAccess && !hasInsightsTrialAccess, watchlist: isWatchlist })}>
      <div className={Styles['investors-header']}>
        Investors
      </div>
      <div className={Styles['investors-body']}>
        {currentData.loading ? (
          <div className={Styles['investors-body-loading']}>
            <Preloader centered loading static />
          </div>
        ) : (
          <>
            {!currentData.data.length ? (
              <div className={Styles['investors-no-data']}>
                <div className={Styles['investors-no-data-text']}>
                  No data currently available.
                </div>
              </div>
            ) : (
              <>
                {width <= 1024 || isWatchlist ? (
                  <TabTableSection
                    currentPage={currentPage}
                    pageSize={Number(pageSize)}
                    currentData={currentData}
                    totalValue={totalValue}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sortField={sortField}
                    hasInsightsAccess={hasInsightsAccess || hasInsightsTrialAccess}
                    hasInsightsTrialAccess={hasInsightsTrialAccess}
                  />
                ) : (
                  <TableSection
                    currentPage={currentPage}
                    pageSize={Number(pageSize)}
                    currentData={currentData}
                    totalValue={totalValue}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sortField={sortField}
                    hasInsightsAccess={hasInsightsAccess || hasInsightsTrialAccess}
                    hasInsightsTrialAccess={hasInsightsTrialAccess}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      {!(currentPage === 1 && !currentData.loading && !currentData.data.length) && (
        <div className={Styles['investors-pagination']}>
          <PerPage
            selectedValue={pageSize}
            setSelectedValue={pageSizeChange}
            fixedSize
            fixedSizeFilterData={perPageFilterDataTablet}
            isDisable={!hasInsightsAccess}
            labelClassName={Styles['investors-pagination-label']}
            tooltipProps={{
              hasBigScreenDesign: true,
              isCentered: true,
              dynamicPosition: true,
              contentStyle: { left: 0 },
              className: Styles['investors-page-tooltip'],
            }}
            tooltipMessage={disableMessage}
            tooltipDisable={!hasInsightsAccess}
          />
          <div className={Styles['investors-navigation']}>
            <div className={Styles['investors-label']}>
              Page:
            </div>
            <ConditionalTooltip
              tooltipProps={{
                hasBigScreenDesign: true,
                isCentered: true,
                dynamicPosition: true,
                contentStyle: { left: 0 },
                className: Styles['investors-tooltip'],
              }}
              tooltipMessage={disableMessage}
              showTooltip={!hasInsightsAccess}
            >
              <div
                className={cx('investors-prev', {
                  disable: currentPage === 1 || currentData.loading || !hasInsightsAccess,
                })}
                onClick={() => currentPageChange('prev')}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                Prev.
              </div>
            </ConditionalTooltip>
            <ConditionalTooltip
              tooltipProps={{
                hasBigScreenDesign: true,
                isCentered: true,
                dynamicPosition: true,
                contentStyle: { left: 0 },
                className: Styles['investors-tooltip'],
              }}
              tooltipMessage={disableMessage}
              showTooltip={!hasInsightsAccess}
            >
              <div
                className={cx('investors-next', {
                  disable: !hasNextPage || currentData.loading || !hasInsightsAccess,
                })}
                onClick={() => currentPageChange('next')}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </ConditionalTooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(Investors);
